import { useState, useEffect } from 'react';

import path from 'path';

import {
	ArrowCircleDown as ArrowCircleDownIcon,
	Cancel as CancelIcon,
	Check as CheckIcon,
	Close as CloseIcon,
	Edit as EditIcon,
	KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon,
	MoneyOff as MoneyOffIcon,
	Stop as StopIcon,
} from '@mui/icons-material';
import { Typography, Hidden, Box, CircularProgress } from '@mui/material';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';

import { FormDialog, AdjustedSkeleton, AlertDialog, StyledButton } from '~components';
import { useAuthorize } from '~features/authentication';
import { DeviceCommandButtonGroup } from '~features/devices';
import {
	CancelOneOffPaymentsForm,
	OneOffPaymentsService,
	RefundNew,
	RefundOneOffPaymentsForm,
} from '~features/finances';
import { OrganisationsService } from '~features/organisations';
import { useSnackbar } from '~hooks';

import AttentionDialog from './AttentionDialog';
import RefundDialog from './RefundDialog';
import { useStyles } from './style';
import { useDetailPageBackButton, useError, useWizardFormField } from '../../../../shared/hooks';
import { isObject, isFullString, isArray } from '../../../../shared/utility';
import * as actions from '../../../../store/actions';
import events from '../../../../store/actions/eventServices';
import { StopBookingDialog } from '../../../Actions';
import SideDrawer from '../../../Planboard/SideDrawer';
import { userInfoAtom } from '~atoms';

const oneOffPaymentsService = new OneOffPaymentsService();
const organisationsService = new OrganisationsService();

interface UserAction {
	id: string;
	type: 'refund' | 'cancelPayments';
}

interface HeaderProps {
	className?: string;
	bookingData?: object;
	loading?: boolean;
	canceledBooking?: {
		success?: boolean;
		loading?: boolean;
		error?: object | string;
	};
	confirmBooking?: {
		success?: boolean;
		loading?: boolean;
		error?: object | string;
	};
	rejectBooking?: {
		success?: boolean;
		loading?: boolean;
		error?: object | string;
	};
	refundBooking?: {
		success?: boolean;
		loading?: boolean;
		error?: object | string;
	};
	currentUser?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
	refundPrice?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
	onFetchBooking?(...args: unknown[]): unknown;
	onCancelBooking?(...args: unknown[]): unknown;
	userData?: object;
	onConfirmBooking?(...args: unknown[]): unknown;
	onRejectBooking?(...args: unknown[]): unknown;
	onUpdateDetailsState?(...args: unknown[]): unknown;
	onResetState?(...args: unknown[]): unknown;
}

const Header = (props: HeaderProps) => {
	const {
		className,
		bookingData,
		loading,
		canceledBooking,
		onFetchBooking,
		onCancelBooking,
		confirmBooking,
		rejectBooking,
		onConfirmBooking,
		onRejectBooking,
		refundBooking,
		onUpdateDetailsState,
		refundPrice,
		onResetState,
	} = props;

	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const userInfo = useAtomValue(userInfoAtom);
	const { authorizeForOrganisation, isSuperAdmin } = useAuthorize();
	const { enqueueSuccessSnackbar, enqueueAxiosErrorSnackbar } = useSnackbar();

	const [userAction, setUserAction] = useState<UserAction | null>(null);

	const [openDialog, setOpenDialog] = useState(false);

	const [openStopBooking, setOpenStopBooking] = useState(false);
	const kilometres = useWizardFormField('', {
		required: true,
		minLength: 1,
		maxLength: 10,
		isNumeric: true,
	});

	const isRejected =
		isObject(bookingData) &&
		bookingData.approvalState === 'pending' &&
		bookingData.status !== 'cancelled';

	const { data: refundPriceData } = refundPrice;
	const classes = useStyles();

	const [openAddOrEditBooking, setOpenAddOrEditBooking] = useState(false);
	const [presetBookingData, setPresetBookingData] = useState(null);

	const [openAttentionDialog, setOpenAttentionDialog] = useState(false);

	const itemReference = isObject(bookingData) && bookingData.itemReference;

	const returnButton = useDetailPageBackButton(location);

	const organisationId = bookingData?.hubReference?.organisationReference?.id;
	const userOrganisationId = userInfo?.organisation?.id;
	const isAuthorizedForOrganisation = authorizeForOrganisation(organisationId);
	const { data: organisation } = useSWR(
		userOrganisationId != null ? [organisationsService.basePath, userOrganisationId] : null,
		([_, id]) => organisationsService.getOrganisationById(String(id))
	);
	const { data: partnerships } = useSWR(
		organisationId != null && userOrganisationId != null ?
			`partnerships?pageNumber=1&pageSize=50&status=all&type=all`
		:	null,
		(path) => events.get(path)
	);
	const partnershipsResults: { partnerOrganisation?: { id?: string | number } }[] =
		isArray(partnerships?.results) ? partnerships.results : [];
	const partnershipsReady = isArray(partnershipsResults);
	const hasAcceptedPartnership =
		partnershipsReady &&
		partnershipsResults.some(
			(partner) => String(partner?.partnerOrganisation?.id) === String(organisationId)
		);
	const isAllowedContractor = organisation?.allowCommands && hasAcceptedPartnership;

	const canUseCommands = isAuthorizedForOrganisation || isAllowedContractor;
	const isVisibleLockUnlock =
		['bmwCar', 'vehicleTrackerCan', 'cpacBoat'].includes(bookingData?.itemInstance?.deviceType) &&
		bookingData.status !== 'completed' &&
		canUseCommands;

	const [approveRequestLoading, setApproveRequestLoading] = useState(false);
	const [rejectRequestLoading, setRejectRequestLoading] = useState(false);

	const { trigger: triggerOneOffRefund, isMutating: isOneOffRefundMutating } = useSWRMutation(
		[oneOffPaymentsService.basePath, oneOffPaymentsService.refundsSubPath],
		(_, { arg }: { arg: { oneOffPaymentId: string; data: RefundNew } }) =>
			oneOffPaymentsService.refundOneOffPayment(arg.oneOffPaymentId, arg.data),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(t('refundSuccessfullySubmitted'));
				setUserAction(null);

				mutate([oneOffPaymentsService.basePath, { bookingId: bookingData?.id }]);
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	const { trigger: triggerCancelOneOffPayment, isMutating: isCancelOneOffPaymentMutating } =
		useSWRMutation(
			[oneOffPaymentsService.basePath],
			(_, { arg }: { arg: { oneOffPaymentId: string } }) =>
				oneOffPaymentsService.cancelOneOffPayment(arg.oneOffPaymentId),
			{
				onSuccess: () => {
					enqueueSuccessSnackbar(t('views.bookings.successfullyCanceledPayment'));
					setUserAction(null);

					mutate([oneOffPaymentsService.basePath, { bookingId: bookingData?.id }]);
					onFetchBooking(bookingData.id);
				},
				onError: (error) => enqueueAxiosErrorSnackbar(error),
			}
		);

	const handleLabel = () => {
		if (returnButton.isBackButton) {
			return t('ui.back');
		} else if (isObject(location) && isFullString(location?.state?.start)) {
			return t('ui.finance');
		} else if (location?.state?.status || location?.state?.label) {
			return location?.state.label;
		} else {
			return t('views.bookings.page.title');
		}
	};

	const handleBackButtonChange = () => {
		if (location?.state?.backPathname) {
			navigate(location.state.backPathname);
		} else if (location?.state?.status) {
			navigate(location.state.from, { state: location?.state });
		} else {
			navigate('/bookings');
			// handleClickBackButton(
			// 	'state' in location ? location : '/bookings',
			// 	'state' in location ? '/bookings' : undefined,
			// );
		}
	};

	const {
		success: canceledSuccess,
		loading: canceledLoading,
		error: canceledError,
	} = canceledBooking;
	const canceledDone = canceledSuccess && !canceledLoading && !canceledError;
	const { success: refundSuccess, loading: refundLoading } = refundBooking;

	const refundProps = {
		openRefundDialog: userAction?.type === 'refund' && bookingData?.paymentType !== 'oneOffPayment',
		priceTotal: bookingData?.price?.total?.total,
		price: bookingData?.price,
		bookingId: bookingData?.id,
	};

	useEffect(() => {
		if (refundSuccess) {
			setUserAction(null);
			if (isObject(refundPriceData)) {
				onUpdateDetailsState('fetchBooking', {
					...bookingData,
					price: {
						...bookingData?.price,
						price: refundPriceData?.totalPriceIncludingVat,
						total: { ...bookingData?.price.total, total: refundPriceData?.totalPriceIncludingVat },
					},
				});
			}
			onResetState('refundPrice');
		}
	}, [refundLoading]);

	const cancelMessage = useError({
		value: canceledBooking,
		message: `${t('ui.successfully')} ${t('ui.canceled')}  ${t('ui.booking')} `,
	});

	const confirmBookingMessage = useError({
		value: confirmBooking,
		message: t('views.bookings.successfullyConfirmedBooking'),
	});

	const rejectBookingMessage = useError({
		value: rejectBooking,
		message: t('views.bookings.successfullyRejectedBooking'),
	});

	const handleCancelOneOffPayments = async (
		refunds: (RefundNew & { oneOffPaymentId: string })[]
	) => {
		const promises = [];
		for (const refund of refunds) {
			const { oneOffPaymentId, ...rest } = refund;
			promises.push(triggerCancelOneOffPayment({ oneOffPaymentId: oneOffPaymentId }));
		}

		Promise.all(promises).then((el) => console.log(el));
	};

	const handleRefundOneOffPayments = async (
		refunds: (RefundNew & { oneOffPaymentId: string })[]
	) => {
		const promises = [];
		for (const refund of refunds) {
			const { oneOffPaymentId, ...rest } = refund;
			promises.push(triggerOneOffRefund({ oneOffPaymentId: oneOffPaymentId, data: rest }));
		}

		Promise.all(promises).then((el) => console.log(el));
	};

	const handleCancelBooking = () => {
		setOpenDialog(true);
	};

	const handleCancelDialog = () => {
		setOpenDialog(false);
	};

	const handleConfirmDialogDisable = () => {
		onCancelBooking(bookingData.id, true);
		cancelMessage.setStartAction(true);
	};

	const handleEdit = () => {
		setPresetBookingData({
			bookingId: Number(bookingData.id),
			itemId: Number(itemReference.id),
			itemSelected: true,
		});
		setOpenAddOrEditBooking(true);
	};

	const handleEditClose = () => {
		setOpenAddOrEditBooking(false);
	};

	const handleStopBooking = () => {
		setOpenStopBooking(true);
		kilometres.resetToInitialValue();
	};

	const carBooking =
		isObject(bookingData) &&
		isFullString(bookingData.usageState) &&
		bookingData.usageState === 'started';

	const handleApproveBookingRequest = async () => {
		setApproveRequestLoading(true);
		await onConfirmBooking(bookingData.id, true);
		confirmBookingMessage.setStartAction(true);
		setApproveRequestLoading(false);
	};

	const handleRejectBookingRequest = async () => {
		setRejectRequestLoading(true);
		await onRejectBooking(bookingData.id, true);
		rejectBookingMessage.setStartAction(true);
		setRejectRequestLoading(false);
	};

	const attentionDialogProps = {
		openAttentionDialog,
		setOpenAttentionDialog,
		data: bookingData,
	};

	const readyToStart =
		bookingData?.isCancellable &&
		bookingData.status === 'active' &&
		bookingData.usageState === 'ready';

	const attention =
		(bookingData?.attentionStates?.includes('startMileage') ||
			bookingData?.attentionStates?.includes('stopMileage')) &&
		isSuperAdmin();

	const buttons = [
		{
			name: 'cancelPayments',
			icon: <MoneyOffIcon />,
			label: t('cancelResource', { resource: t('payments') }),
			visible: isSuperAdmin() && bookingData?.hasOpenOneOffPayments,
			onClick: () => setUserAction({ id: bookingData.id.toString(), type: 'cancelPayments' }),
		},
		{
			name: 'refund',
			icon: <ArrowCircleDownIcon />,
			label: t('views.bookings.bookingDetails.button.inline.refund'),
			visible:
				isSuperAdmin() &&
				isObject(bookingData) &&
				(bookingData.paymentType === 'oneOffPayment' ?
					bookingData.hasPaidOneOffPayments
				:	bookingData.status === 'completed') &&
				!bookingData.price.isFree &&
				bookingData.attentionStates.includes('none'),
			className: classes.buttons,
			onClick: () => setUserAction({ id: bookingData.id.toString(), type: 'refund' }),
			variant: 'inline-default',
		},
		{
			name: 'stop',
			icon: <StopIcon />,
			label: t('view.bookings.bookingDetails.button.inline.stop'),
			visible:
				isObject(bookingData) &&
				!bookingData.isCancellable &&
				(bookingData.status === 'active' || bookingData.status === 'overtime'),
			className: classes.buttonsCancel,
			onClick: handleStopBooking,
			variant: 'inline-delete',
		},
		{
			name: 'cancel',
			icon: <CancelIcon />,
			label: t('view.bookings.bookingDetails.button.inline.cancel'),
			visible:
				isObject(bookingData) &&
				((bookingData.isCancellable && !carBooking && bookingData.status !== 'active') ||
					readyToStart),
			className: classes.buttonsCancel,
			onClick: handleCancelBooking,
			variant: 'inline-delete',
		},
		{
			name: 'edit',
			icon: <EditIcon />,
			label: t('view.bookings.bookingDetails.button.inline.edit'),
			visible:
				isObject(bookingData) &&
				((bookingData.status !== 'completed' && bookingData.status !== 'cancelled') ||
					(bookingData.status === 'completed' && attention) ||
					(bookingData.approvalState !== 'declined' &&
						bookingData.approvalState !== 'notApplicable')),
			className: classes.buttons,
			variant: 'inline-default',
			onClick: handleEdit,
		},
		...(isRejected && authorizeForOrganisation(bookingData?.hubReference.organisationReference.id) ?
			[
				{
					name: 'approve',
					icon:
						!approveRequestLoading ? <CheckIcon /> : <CircularProgress color='inherit' size={18} />,
					disabled: approveRequestLoading,
					label: t('ui.approveRequest'),
					visible: isObject(bookingData) && bookingData.status !== 'completed',
					className: classes.buttons,
					variant: 'inline-default',
					onClick: handleApproveBookingRequest,
				},
				{
					name: 'reject',
					icon:
						!rejectRequestLoading ? <CloseIcon /> : <CircularProgress color='inherit' size={18} />,
					disabled: rejectRequestLoading,
					label: t('ui.rejectRequest'),
					visible: isObject(bookingData) && bookingData.status !== 'completed',
					className: classes.buttonsCancel,
					variant: 'inline-default',
					onClick: handleRejectBookingRequest,
				},
			]
		:	[]),
		{
			name: 'approve booking',
			icon: <CheckIcon />,
			label: t('views.bookings.bookingDetails.button.attention'),
			visible:
				isSuperAdmin() &&
				!bookingData?.attentionStates.includes('none') &&
				bookingData?.status === 'completed',
			className: classes.attention,
			variant: 'inline-default',
			onClick: () => setOpenAttentionDialog(true),
		},
	];

	return (
		<div className={clsx(classes.root, className)}>
			<Typography component='h2' gutterBottom variant='overline'>
				<Hidden only={['xs', 'sm']}>
					<StyledButton
						className={classes.backButton}
						onClick={handleBackButtonChange}
						startIcon={<KeyboardArrowLeftOutlinedIcon />}
						variant='inline-default'
					>
						{handleLabel()}
					</StyledButton>
				</Hidden>
			</Typography>
			<Typography component='h1' variant='h3'>
				<Box display='flex' justifyContent='space-between'>
					{loading ?
						<AdjustedSkeleton animation='wave' width={200} />
					:	`${t('ui.label.booking')} ${bookingData.id}`}
					{loading ?
						<AdjustedSkeleton animation='wave' width={360} />
					:	<Box
							display='flex'
							flexDirection={
								isSuperAdmin() && !bookingData?.attentionStates.includes('none') ?
									'row-reverse'
								:	'row'
							}
						>
							{buttons.map((button) =>
								button.visible ?
									<StyledButton
										className={button.className}
										key={button.name}
										onClick={button.onClick}
										size='small'
										startIcon={button.icon}
										variant={button.variant}
										disabled={button.disabled}
									>
										{button.label}
									</StyledButton>
								:	null
							)}
							{isVisibleLockUnlock ?
								<Box pl={1}>
									<DeviceCommandButtonGroup itemInstance={bookingData.itemInstance} />
								</Box>
							:	null}
						</Box>
					}
				</Box>
			</Typography>
			{openDialog ?
				<AlertDialog
					dialogDescription={t('views.planboard.editBooking.cancelAlert.description')}
					dialogTitle={t('views.planboard.editBooking.cancelAlert.title')}
					handleClose={handleCancelDialog}
					handleConfirm={handleConfirmDialogDisable}
					loading={canceledLoading && !canceledDone}
					open={openDialog}
				/>
			:	null}
			<SideDrawer
				onClose={handleEditClose}
				open={openAddOrEditBooking}
				presetData={presetBookingData}
				setPresetBookingData={setPresetBookingData}
			/>
			{openStopBooking ?
				<StopBookingDialog
					data={bookingData}
					end={bookingData.periodEnd}
					itemInstance={bookingData.itemInstance}
					kilometres={kilometres}
					open={openStopBooking}
					setOpenStopBooking={setOpenStopBooking}
					start={bookingData.periodStart}
				/>
			:	null}
			{userAction?.type === 'refund' && bookingData?.paymentType !== 'oneOffPayment' ?
				<RefundDialog {...refundProps} onClose={() => setUserAction(null)} />
			:	null}
			{openAttentionDialog ?
				<AttentionDialog {...attentionDialogProps} />
			:	null}

			<FormDialog
				title={t('views.bookings.bookingDetails.refundDialog.title')}
				open={userAction?.type === 'refund' && bookingData?.paymentType === 'oneOffPayment'}
				onClose={() => setUserAction(null)}
				maxWidth='lg'
				loading={isOneOffRefundMutating}
				saveLabel={t('ui.refund')}
			>
				<RefundOneOffPaymentsForm
					bookingId={bookingData?.id.toString()}
					onSubmit={(data) => handleRefundOneOffPayments(data)}
					totalBookingAmount={bookingData?.price.price}
				/>
			</FormDialog>

			<FormDialog
				title={t('views.bookings.bookingDetails.cancelPayments.title')}
				open={userAction?.type === 'cancelPayments'}
				onClose={() => setUserAction(null)}
				maxWidth='lg'
				loading={isCancelOneOffPaymentMutating}
				saveLabel={t('confirm')}
			>
				<CancelOneOffPaymentsForm
					bookingId={bookingData?.id.toString()}
					onSubmit={(data) => handleCancelOneOffPayments(data)}
					totalBookingAmount={bookingData?.price.price}
				/>
			</FormDialog>
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		canceledBooking: state.condition.canceledBooking,
		confirmBooking: state.condition.confirmBooking,
		rejectBooking: state.condition.rejectBooking,
		refundBooking: state.condition.refundBooking,
		refundPrice: state.details.refundPrice,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		onFetchBooking: (id) => dispatch(actions.fetchBooking(id)),
		onCancelBooking: (id, redirect) => dispatch(actions.cancelBooking(id, redirect)),
		onConfirmBooking: (bookingId, fetch) => dispatch(actions.confirmBooking(bookingId, fetch)),
		onRejectBooking: (bookingId, fetch) => dispatch(actions.rejectBooking(bookingId, fetch)),
		onUpdateDetailsState: (state, data) => dispatch(actions.updateDetailsState(state, data)),
		onResetState: (identifier) => dispatch(actions.resetState(identifier)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
